/**
 * Relief « massif » : des montagnes décrites par leurs lignes de crête.
 *
 * Chaque arête est une polyligne dont chaque point porte son altitude. De
 * part et d'autre descendent des versants concaves : raides sous la crête,
 * adoucis vers le pied, comme une vraie pente de montagne. Deux arêtes qui
 * divergent d'un sommet dessinent une combe entre elles ; plusieurs arêtes
 * rayonnant d'un sommet donnent autant de versants d'orientations
 * différentes (adret au sud, ubac au nord, faces est et ouest).
 *
 * Le bruit ajoute ce qui rend le relief crédible : lignes de crête sinueuses,
 * crêtes rocheuses, couloirs qui suivent la ligne de pente, éperons et
 * renfoncements, détail fin. Des replats (alpages, cols, fonds de vallon)
 * aplanissent localement le relief.
 *
 * Fonction pure de la description et de la graine : le serveur et le client
 * obtiennent le même relief au bit près (opérations de base et racines
 * carrées uniquement, aucune fonction transcendante).
 */
import { createNoise2D, fbm, ridged, smoothstep, clamp, lerp, swave } from './noise';

/** Ligne de crête. */
export interface RidgeSpec {
  /** Points de la crête : [x, z, altitude] (m). */
  pts: [number, number, number][];
  /** Pente moyenne des versants, du faîte au pied (0,32 = 32 %). */
  grade?: number;
  /**
   * Concavité du profil (0..1) : 0, pente régulière ; 1, très raide sous la
   * crête et très douce au pied (larges fonds de vallée pour débutants).
   */
  concave?: number;
  /** Altitude du pied des versants (m) ; par défaut, le fond de vallée. */
  foot?: number;
}

/** Replat : le relief s'y aplanit vers une altitude. */
export interface FlatSpec {
  x: number;
  z: number;
  /** Rayon (m) : plat au centre, raccordé au relief jusqu'au bord. */
  radius: number;
  /** Altitude visée (m). */
  h: number;
}

export interface MassifRelief {
  kind: 'massif';
  /** Altitude du fond de vallée au centre de la carte (m). */
  floor: number;
  /** Pente régionale du fond de vallée [par m vers l'est, par m vers le sud] : vallons perchés, vallée qui s'abaisse. */
  tilt?: [number, number];
  ridges: RidgeSpec[];
  flats?: FlatSpec[];
}

/** Pente par défaut des versants. */
const DEFAULT_GRADE = 0.32;
/** Concavité par défaut du profil des versants. */
const DEFAULT_CONCAVE = 0.4;
/** Au-delà du pied, un versant perché redescend doucement vers la vallée. */
const FAR_GRADE = 0.05;
/** Arrondi des fonds de combe, là où deux versants se rencontrent (m). */
const BLEND = 48;
/** Amplitude des brèches et ressauts le long des crêtes (m). */
const CREST_NOISE = 28;

interface Segment {
  ax: number;
  az: number;
  dx: number;
  dz: number;
  l2: number;
  len: number;
  ha: number;
  hb: number;
  /** Abscisse le long de la crête au point a (m). */
  s0: number;
}

interface Ridge {
  segs: Segment[];
  grade: number;
  concave: number;
  /** Pied imposé (versant perché), sinon le fond de vallée local. */
  foot: number | null;
  /** Décalage propre à l'arête dans le bruit (crêtes et couloirs différents). */
  phase: number;
}

/** Maximum adouci : raccord arrondi de largeur k entre deux surfaces. */
function smax(a: number, b: number, k: number): number {
  const h = clamp(0.5 + (0.5 * (a - b)) / k, 0, 1);
  return b + (a - b) * h + k * h * (1 - h);
}

function prepare(r: RidgeSpec, index: number): Ridge {
  const segs: Segment[] = [];
  let s = 0;
  const pts = r.pts;
  if (pts.length === 1) {
    const p = pts[0] as [number, number, number];
    segs.push({ ax: p[0], az: p[1], dx: 0, dz: 0, l2: 0, len: 0, ha: p[2], hb: p[2], s0: 0 });
  }
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i] as [number, number, number];
    const b = pts[i + 1] as [number, number, number];
    const dx = b[0] - a[0];
    const dz = b[1] - a[1];
    const l2 = dx * dx + dz * dz;
    const len = Math.sqrt(l2);
    segs.push({ ax: a[0], az: a[1], dx, dz, l2, len, ha: a[2], hb: b[2], s0: s });
    s += len;
  }
  return { segs, grade: r.grade ?? DEFAULT_GRADE, concave: clamp(r.concave ?? DEFAULT_CONCAVE, 0, 1), foot: r.foot ?? null, phase: index * 7.31 + 0.5 };
}

/** Altitude brute du massif en (x, z), avant village et lac. */
export function createMassifFunction(relief: MassifRelief, seed: number): (x: number, z: number) => number {
  const nWarpX = createNoise2D(seed + 101);
  const nWarpZ = createNoise2D(seed + 113);
  const nCrest = createNoise2D(seed + 127);
  const nGully = createNoise2D(seed + 131);
  const nPeaks = createNoise2D(seed + 11);
  const nDetail = createNoise2D(seed + 37);
  const nFloor = createNoise2D(seed + 67);
  const floor = relief.floor;
  const [tiltX, tiltZ] = relief.tilt ?? [0, 0];
  const ridges = relief.ridges.map((r, i) => prepare(r, i));
  const flats = relief.flats ?? [];
  let top = floor + 1;
  for (const r of relief.ridges) for (const p of r.pts) top = Math.max(top, p[2]);
  const span = top - floor;
  let maxSegs = 1;
  for (const r of ridges) maxSegs = Math.max(maxSegs, r.segs.length);
  const segD = new Float64Array(maxSegs);
  const segS = new Float64Array(maxSegs);
  const segH = new Float64Array(maxSegs);

  return (x: number, z: number): number => {
    // Lignes de crête sinueuses : on lit les arêtes dans un repère déformé (grandes inflexions et zigzags).
    const wx = x + 95 * fbm(nWarpX, x / 760, z / 760, 2) + 30 * nWarpZ(x / 230 + 5.3, z / 230 - 2.1);
    const wz = z + 95 * fbm(nWarpZ, x / 760 + 13.1, z / 760 - 7.7, 2) + 30 * nWarpX(x / 230 - 8.9, z / 230 + 4.4);
    const base = floor + tiltX * x + tiltZ * z;
    // Versants combinés : maximum adouci entre arêtes (fonds de combe arrondis).
    let m = -Infinity;
    for (const r of ridges) {
      // Distance à chaque segment de la crête, abscisse et altitude du point le plus proche.
      let d = Infinity;
      const n = r.segs.length;
      for (let k = 0; k < n; k++) {
        const g = r.segs[k] as Segment;
        let t = g.l2 > 0 ? ((wx - g.ax) * g.dx + (wz - g.az) * g.dz) / g.l2 : 0;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const px = wx - (g.ax + g.dx * t);
        const pz = wz - (g.az + g.dz * t);
        const dk = Math.sqrt(px * px + pz * pz);
        segD[k] = dk;
        segS[k] = g.s0 + g.len * t;
        segH[k] = g.ha + (g.hb - g.ha) * t;
        if (dk < d) d = dk;
      }
      // Au creux d'un coude, le point le plus proche saute d'un segment à l'autre : on mélange
      // les segments presque aussi proches pour que l'altitude de crête reste continue.
      const tau = 30 + 0.3 * d;
      let wsum = 0;
      let s = 0;
      let hc = 0;
      for (let k = 0; k < n; k++) {
        const wk = smoothstep(tau, 0, (segD[k] as number) - d);
        if (wk <= 0) continue;
        wsum += wk;
        s += wk * (segS[k] as number);
        hc += wk * (segH[k] as number);
      }
      s /= wsum;
      hc /= wsum;
      const foot = r.foot ?? base;
      // Hors de portée d'un versant ordinaire : il passe sous le fond de vallée, inutile d'aller plus loin.
      if (r.foot === null && d * r.grade >= (hc + CREST_NOISE - foot) * 1.17) continue;
      // Crête irrégulière : brèches et ressauts le long de l'arête.
      hc += CREST_NOISE * fbm(nCrest, s / 260 + r.phase, r.phase * 0.37, 2);
      const rel = hc - foot;
      if (rel <= 1) continue;
      // Éperons et renfoncements : le versant s'allonge ou se creuse selon l'endroit.
      const reach = (rel / r.grade) * (1 + 0.17 * swave(s / 880 + r.phase * 0.21));
      const u = d / reach;
      let face: number;
      if (u < 1) {
        const w = 1 - u;
        // Profil concave : entre w^1,5 (régulier) et w² (fond de vallée très doux).
        face = foot + rel * w * (Math.sqrt(w) + (w - Math.sqrt(w)) * r.concave);
        // Couloirs dans la ligne de pente, plus marqués à mi-versant.
        const g = 1 - Math.abs(swave(s / 720 + 0.37 + r.phase + 0.15 * nGully(x / 420, z / 420)));
        face -= Math.min(16, 0.022 * rel) * g * g * (3 - 2 * g) * 4 * u * w;
      } else if (r.foot !== null) {
        face = foot - FAR_GRADE * (d - reach);
      } else {
        continue;
      }
      // Arrondi proportionnel à la hauteur : net en crête, nul au ras du fond de vallée (raccord continu au pied).
      if (m === -Infinity) m = face;
      else {
        const k = BLEND * clamp((Math.min(m, face) - base) / 150, 0, 1);
        m = k > 0.5 ? smax(m, face, k) : m > face ? m : face;
      }
    }
    // Un versant rejoint le fond de vallée en tangente : un maximum simple suffit.
    let h = m > base ? m : base;
    const hn = clamp((h - floor) / span, 0, 1);
    // Crêtes et sommets rocheux (moitié haute du massif seulement).
    if (hn > 0.5) h += 80 * ridged(nPeaks, x / 540 + 3.7, z / 540 - 1.3, 4) * smoothstep(0.5, 1, hn) * hn * hn;
    // Détail fin, plus marqué en altitude ; fond de vallée légèrement ondulé.
    h += fbm(nDetail, x / 190, z / 190, 4) * (3 + 9 * hn);
    h += 6 * fbm(nFloor, x / 240, z / 240, 3) * (1 - hn);
    for (const f of flats) {
      const dx = x - f.x;
      const dz = z - f.z;
      const w = smoothstep(f.radius, f.radius * 0.45, Math.sqrt(dx * dx + dz * dz));
      if (w > 0) h = lerp(h, f.h + 0.15 * (h - f.h) * (1 - w), w);
    }
    return h;
  };
}
